import React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useKeycloak } from '../context/KeycloakContext';
import { useAuth } from '../context/AuthContext';
import env from '../config/env';
import LoadingSpinner from './LoadingSpinner';
import RoleService from '../services/RoleService';

const ProtectedRoute = () => {
    const useKeycloakAuth = env.useKeycloakAuth;
    const { keycloak, isAuthenticated: isKeycloakAuthenticated, isLoading: isKeycloakLoading } = useKeycloak();
    const { isAuthenticated: isCustomAuthenticated } = useAuth();
    const location = useLocation();

    // If environment config is still loading, wait without showing anything
    if (env.isLoading) {
        return null;
    }

    // For Keycloak authentication
    if (useKeycloakAuth) {
        // If still loading, show loading spinner
        if (isKeycloakLoading) {
            return <LoadingSpinner />;
        }

        // If not authenticated, redirect to Keycloak login
        if (!isKeycloakAuthenticated) {
            keycloak.login();
            return null;
        }

        // Check for queue access if path starts with /queues
        if (location.pathname.startsWith('/queues/') && !RoleService.hasQueueViewRole()) {
            return <Navigate to="/dashboard" replace />;
        }

        // Check for config management access
        if ((location.pathname === '/queues' || location.pathname === '/codex-types') && !RoleService.hasConfigManageRole()) {
            return <Navigate to="/dashboard" replace />;
        }

        // Check for advanced config management access
        if (['/registries', '/prompts', '/processes', '/test-validation', '/system-settings'].includes(location.pathname) && !RoleService.hasConfigAdvancedRole()) {
            return <Navigate to="/dashboard" replace />;
        }

        // If authenticated and has access, render the protected content
        return <Outlet />;
    }

    // For custom authentication
    const isAuthenticated = isCustomAuthenticated;

    // Check for queue access if path starts with /queues for custom auth
    if (location.pathname.startsWith('/queues/') && !RoleService.hasQueueViewRole()) {
        return <Navigate to="/dashboard" replace />;
    }

    // Check for config management access for custom auth
    if ((location.pathname === '/queues' || location.pathname === '/codex-types') && !RoleService.hasConfigManageRole()) {
        return <Navigate to="/dashboard" replace />;
    }

    // Check for advanced config management access for custom auth
    if (['/registries', '/prompts', '/processes', '/test-validation', '/system-settings'].includes(location.pathname) && !RoleService.hasConfigAdvancedRole()) {
        return <Navigate to="/dashboard" replace />;
    }

    return isAuthenticated ? <Outlet /> : <Navigate to="/login" />;
};

export default ProtectedRoute; 